import { useState, useRef, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories, MAIN_SITE_URL } from "@/mocks/categories";

const languages = [
  { code: "en", label: "English", flag: "🇬🇧" },
  { code: "fr", label: "Français", flag: "🇫🇷" },
  { code: "es", label: "Español", flag: "🇪🇸" },
  { code: "pl", label: "Polski", flag: "🇵🇱" },
];

export default function Header() {
  const { t, i18n } = useTranslation();
  const [langOpen, setLangOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace("#", "");
    const timer = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 120);
    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);

  const currentLang = languages.find((l) => l.code === (i18n.language || "en").split("-")[0]) || languages[0];

  function changeLanguage(code: string) {
    i18n.changeLanguage(code);
    setLangOpen(false);
    setMenuOpen(false);
  }

  const isHome = location.pathname === "/";

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background-50 border-b-2 border-background-200">
      <div className="w-full px-4 md:px-6">
        <div className="max-w-[1280px] mx-auto h-16 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0 cursor-pointer" onClick={() => setMenuOpen(false)}>
            <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 font-heading font-bold text-sm">
              U
            </span>
            <span className="font-heading font-bold text-foreground-950 text-base md:text-lg whitespace-nowrap">
              {t("brand")}
            </span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            <Link
              to="/"
              className={`px-3 py-2 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                isHome ? "text-primary-700 bg-primary-100" : "text-foreground-800 hover:text-primary-700 hover:bg-background-100"
              }`}
            >
              {t("nav_home")}
            </Link>
            <Link
              to="/#categories"
              className="px-3 py-2 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer text-foreground-800 hover:text-primary-700 hover:bg-background-100 transition-colors"
            >
              {t("nav_categories")}
            </Link>
            <Link
              to="/shopping-guide"
              className={`px-3 py-2 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                location.pathname === "/shopping-guide"
                  ? "text-primary-700 bg-primary-100"
                  : "text-foreground-800 hover:text-primary-700 hover:bg-background-100"
              }`}
            >
              {t("nav_shop_guide")}
            </Link>
            <Link
              to="/#articles"
              className="px-3 py-2 rounded-md text-sm font-semibold whitespace-nowrap cursor-pointer text-foreground-800 hover:text-primary-700 hover:bg-background-100 transition-colors"
            >
              {t("nav_articles")}
            </Link>
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            {/* Language selector */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen((v) => !v)}
                className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-background-300 bg-background-50 text-sm font-semibold text-foreground-900 cursor-pointer hover:bg-background-100 transition-colors whitespace-nowrap"
              >
                <span>{currentLang.flag}</span>
                <span className="hidden sm:inline">{currentLang.label}</span>
                <i className="ri-arrow-down-s-line text-foreground-500" />
              </button>
              {langOpen && (
                <div className="absolute right-0 mt-2 w-44 rounded-lg border border-background-200 bg-background-50 py-1 z-50">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => changeLanguage(l.code)}
                      className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left cursor-pointer transition-colors hover:bg-background-100 ${
                        l.code === currentLang.code ? "text-primary-600 font-semibold" : "text-foreground-700"
                      }`}
                    >
                      <span>{l.flag}</span>
                      <span>{l.label}</span>
                      {l.code === currentLang.code && <i className="ri-check-line ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Main site CTA */}
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-primary-500 text-background-50 text-sm font-semibold cursor-pointer hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t("nav_visit")}
              <i className="ri-external-link-line" />
            </a>

            {/* Mobile hamburger */}
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-md text-foreground-950 cursor-pointer hover:bg-background-100"
              aria-label="Menu"
            >
              <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          <Link
            to="/"
            onClick={() => setMenuOpen(false)}
            className="px-3 py-3 rounded-md text-foreground-950 font-semibold cursor-pointer hover:bg-background-100 flex items-center gap-2"
          >
            <i className="ri-home-5-line text-primary-500" />
            {t("nav_home")}
          </Link>
          <Link
            to="/shopping-guide"
            onClick={() => setMenuOpen(false)}
            className="px-3 py-3 rounded-md text-foreground-950 font-semibold cursor-pointer hover:bg-background-100 flex items-center gap-2"
          >
            <i className="ri-shopping-bag-3-line text-primary-500" />
            {t("nav_shop_guide")}
          </Link>

          {/* Categories accordion */}
          <button
            onClick={() => setCatsOpen((v) => !v)}
            className="px-3 py-3 rounded-md text-foreground-950 font-semibold cursor-pointer hover:bg-background-100 flex items-center justify-between gap-2"
          >
            <span className="flex items-center gap-2">
              <i className="ri-grid-line text-primary-500" />
              {t("nav_categories")}
            </span>
            <i className={`ri-arrow-down-s-line transition-transform ${catsOpen ? "rotate-180" : ""}`} />
          </button>
          {catsOpen && (
            <div className="grid grid-cols-2 gap-1 px-1 pb-2">
              {categories.map((c) => (
                <a
                  key={c.slug}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  onClick={() => setMenuOpen(false)}
                  className="px-3 py-2 rounded-md text-sm text-foreground-700 cursor-pointer hover:bg-background-100 hover:text-primary-700 transition-colors whitespace-nowrap"
                >
                  {t(`cats.${c.slug}`)}
                </a>
              ))}
            </div>
          )}

          <Link
            to="/#articles"
            onClick={() => setMenuOpen(false)}
            className="px-3 py-3 rounded-md text-foreground-950 font-semibold cursor-pointer hover:bg-background-100 flex items-center gap-2"
          >
            <i className="ri-article-line text-primary-500" />
            {t("nav_articles")}
          </Link>

          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-2 px-3 py-3 rounded-md bg-primary-500 text-background-50 font-semibold cursor-pointer hover:bg-primary-600 text-center flex items-center justify-center gap-2"
          >
            {t("nav_visit")}
            <i className="ri-external-link-line" />
          </a>
        </div>
      )}
    </header>
  );
}